import { ChangeEvent } from "react";
import {
  Box,
  Checkbox,
  Colors,
  RegularInput,
  TextVariants,
  Text,
  CreatorQuestionIds,
  Input,
  LocationAutocomplete,
  LanguageAutocomplete,
  SoftwareAutocomplete,
  Switch,
} from "reusable";
import { Question } from "../Question";
import UploadPhoto from "./UploadPhoto";
import { Location } from "models";

const RenderInputField = (props: {
  question: Question;
  value: string | Array<string> | Array<Record<string, string>> | FileList;
  onChange: (
    event: ChangeEvent<HTMLInputElement>,
    detector: Question,
    subQuestion?: Record<string, string>
  ) => void;
}): JSX.Element => {
  const { question, value, onChange } = props;

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const maxSizeInBytes = 2 * 1024 * 1024; // 2MB

      if (file.size > maxSizeInBytes) {
        event.preventDefault();
        const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
        alert(
          `File size (${fileSizeInMB}MB) exceeds the maximum limit of 2MB. Please compress your PDF and try again.`
        );
        event.target.value = "";
        return;
      }

      if (
        question.acceptedFileTypes &&
        !question.acceptedFileTypes
          .split(",")
          .some((type) => file.name.toLowerCase().endsWith(type.trim().toLowerCase()))
      ) {
        alert(`Only ${question.acceptedFileTypes} files are allowed`);
        event.target.value = "";
        return;
      }

      onChange(event, question);
    }
  };

  const themeColours = [
    { background: "#DDC6E7", color: "#9A67AD" },
    { background: "#B6DBF3", color: "#6BAEF9" },
    { background: "#ACE9DD", color: "#6EC7B5" },
    { background: "#FCDCA9", color: "#F0C3A3" },
    { background: "#F8C0BA", color: "#FF7262" },
    { background: "#E3D1B8", color: "#BBAC98" },
    { background: "#F9FAFC", color: "#1F2937" },
    { background: "#B4BCC2", color: "#575F69" },
  ];

  const getPreIcon = () => {
    if (question?.preIcon === "ContactNumber") {
      return (
        <Box>
          <Text variant={TextVariants.MEDIUM_15_TERNARY_FONT_BOLD} color={Colors.GRAY_2}>
            +91
          </Text>
        </Box>
      );
    }
    return null;
  };

  switch (question.type) {
    case "switch":
      return (
        <>
          <Switch
            isChecked={value as unknown as boolean}
            onChange={(answer) =>
              onChange({ target: { value: answer } } as any, question)
            }
          />
          <Box display="flex" justifyContent="space-between">
            <Text variant={TextVariants.MEDIUM_PRIMARY_FONT} color={Colors.GRAY_5} marginRight="10px">
              {question.questionID === "isHiring"
                ? "Your profile will have a hiring employee banner."
                : "Your profile will have a looking for a job banner."}
            </Text>
          </Box>
        </>
      );

    case "location-autocomplete":
      return (
        <LocationAutocomplete
          selectedLocation={value as unknown as Location}
          onChange={(e) => onChange(e, question)}
        />
      );

    case "language-chip":
      return (
        <LanguageAutocomplete
          onChange={(e) => onChange(e, question)}
          input={value as string}
        />
      );

    case "software-chip":
      return (
        <SoftwareAutocomplete
          onChange={(e) => onChange(e, question)}
          input={value as string}
        />
      );

    case "textArea":
      return (
        <Input
          inputType="textarea"
          placeholder={question?.preIcon ? "" : question?.placeHolder || "Start Typing"}
          value={(value as string) || ""}
          onChange={(e) => onChange(e, question)}
        />
      );

    case "text":
      return (
        <>
          {question?.options?.length ? (
            question.options.map((option, index) => (
              <RegularInput
                key={index}
                placeholder={(option as any).placeholder || "Start Typing"}
                preIcon={getPreIcon()}
                value={
                  value
                    ? (
                        (value as Record<string, string>[])?.find(
                          (v) => v?.questionID === (option as any)?.questionID
                        ) as Record<string, string>
                      )?.[(option as any)?.questionID]
                    : ""
                }
                onChange={(e) =>
                  onChange(e, question, option as Record<string, string>)
                }
              />
            ))
          ) : (
            <RegularInput
              placeholder={question?.preIcon ? "" : question?.placeHolder || "Start Typing"}
              preIcon={getPreIcon()}
              value={(value as string) || ""}
              onChange={(e) => onChange(e, question)}
              className={`regular-input-${question?.type}`}
              type={question?.inputType}
            />
          )}
        </>
      );

    case "checkbox":
      return (
        <Checkbox
          options={String(question.options)?.split(", ") as Array<string>}
          onChange={(e) => onChange(e, question)}
          checkedValues={(String(value)?.split(", ") as Array<string>) || [""]}
          multiple={question?.multiple}
        />
      );

    case "photo":
      return (
        <UploadPhoto
          type={
            question.questionID === CreatorQuestionIds.PROFILE_PICTURE
              ? "profile-photo"
              : "creator-photos"
          }
          onImageChange={(e) => onChange(e, question)}
          existingImages={value as string[] | string}
        />
      );

    case "theme":
      return (
        <Box marginTop="15px">
          <Box className="theme-box-wrapper" marginBottom="25px" display="flex" flexWrap="wrap">
            {themeColours.map((c, index) => (
              <Box
                key={index}
                cursor={c.background ? "pointer" : ""}
                className={`theme-box ${
                  c.background === (value as any)?.background ? "selected-theme" : ""
                }`}
                backgroundColor={c.background || ""}
                onClick={(e: any) => onChange({ ...e, theme: c }, question)}
              />
            ))}
          </Box>
        </Box>
      );

      case "file":
        return (
          <Box>
            <input
              type="file"
              accept={question.acceptedFileTypes}
              onChange={handleFileChange}
              className={`regular-input-${question?.type}`}
              style={{ fontFamily: "Inter", fontSize: "14px" }}
            />
            
            {question.subContent && (
              <Text
                variant={TextVariants.MEDIUM_PRIMARY_FONT}
                color={Colors.GRAY_5}
                marginTop="5px"
              >
                {question.subContent}
              </Text>
            )}
          </Box>
        );

    default:
      return <RegularInput />;
  }
};

export default RenderInputField;
